import {
  base64ToBytes,
  bytesToBase64,
  EXTENSION_GATE_DETECT_TIMEOUT_MS,
  EXTENSION_GATE_MESSAGE_TAG,
  EXTENSION_GATE_RESPONSE_GRACE_MS,
  isBrowser,
} from "@prostocheck/shared";

import {
  IExtensionGateMessage,
  IExtensionGateRequestParams,
  IExtensionGateResponse,
} from "./extension-gate.models";

/**
 * Direct-to-ДПС/ЦСК transport over the jkurwa-web gate browser extension —
 * see ai/docs/kep-signing.md. This is the page side of a protocol fixed by
 * that extension's `content.js`/`background.js`; nothing here invents wire
 * shapes, it only speaks what they already listen for.
 *
 * `libs/kep`'s own tests run under Vitest's Node environment
 * (`libs/kep/vitest.config.ts`), and this module is reached transitively by
 * every one of them once `gateway.ts` imports it — `isBrowser`
 * (`@prostocheck/shared`) guarding the listener below is load-bearing, not
 * defensive boilerplate.
 */

/** `undefined` = not yet probed, `null` = confirmed absent, a string = the extension's version. */
let detectedVersion: string | null | undefined;
const pendingRequests = new Map<string, (message: IExtensionGateMessage) => void>();
let requestSeq = 0;

if (isBrowser) {
  window.addEventListener("message", event => {
    const message = event.data as IExtensionGateMessage | undefined;

    if (event.source !== window || !message || message.tag !== EXTENSION_GATE_MESSAGE_TAG) {
      return;
    }

    if (message.kind === "hello" || message.kind === "pong") {
      detectedVersion = message.version ?? null;

      return;
    }

    if (message.kind === "response" && message.id) {
      const resolve = pendingRequests.get(message.id);

      if (resolve) {
        pendingRequests.delete(message.id);
        resolve(message);
      }
    }
  });
}

/**
 * The extension announces itself unprompted, so this usually resolves without
 * even sending the `ping` below. Meant to be called once, at app start — see
 * `useExtensionGate` (`libs/data-web`).
 *
 * `force` discards a cached `null` (confirmed absent) and probes again —
 * for `useExtensionGateRecheck` (`libs/data-web`), which re-asks whenever the
 * login page is shown and the last answer was "not connected": the user may
 * have installed or enabled the extension since, and a cache that only ever
 * resolves once would never notice.
 */
export const detectExtensionGate = (
  timeoutMs = EXTENSION_GATE_DETECT_TIMEOUT_MS,
  { force = false }: { force?: boolean } = {},
): Promise<boolean> => {
  if (!isBrowser) {
    return Promise.resolve(false);
  }

  if (force) {
    detectedVersion = undefined;
  }

  if (detectedVersion !== undefined) {
    return Promise.resolve(detectedVersion !== null);
  }

  window.postMessage(
    { tag: EXTENSION_GATE_MESSAGE_TAG, kind: "ping" },
    window.location.origin,
  );

  return new Promise(resolve => {
    const pollInterval = setInterval(() => {
      if (detectedVersion !== undefined) {
        clearInterval(pollInterval);
        resolve(detectedVersion !== null);
      }
    }, 30);

    setTimeout(() => {
      clearInterval(pollInterval);
      detectedVersion = detectedVersion ?? null;
      resolve(detectedVersion !== null);
    }, timeoutMs);
  });
};

/** Synchronous — the check `gatewayRequest` makes on every call. */
export const isExtensionGateAvailable = (): boolean =>
  isBrowser && Boolean(detectedVersion);

export const getExtensionGateVersion = (): string | null => detectedVersion ?? null;

/**
 * Data travels base64-encoded because `chrome.runtime.sendMessage` (inside
 * `content.js`) JSON-serialises its payload — see `@prostocheck/shared`'s
 * `bytesToBase64`/`base64ToBytes`.
 */
export const requestViaExtensionGate = ({
  url,
  body,
  contentType,
  timeoutMs,
}: IExtensionGateRequestParams): Promise<IExtensionGateResponse> => {
  const id = `r${++requestSeq}`;

  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      pendingRequests.delete(id);
      reject(new Error("jkurwa-web gate extension did not answer in time"));
    }, timeoutMs + EXTENSION_GATE_RESPONSE_GRACE_MS);

    pendingRequests.set(id, message => {
      clearTimeout(timer);

      if (!message.ok) {
        reject(new Error(message.error ?? "jkurwa-web gate extension request failed"));

        return;
      }

      resolve({
        status: message.status ?? 0,
        body: base64ToBytes(message.body ?? ""),
      });
    });

    window.postMessage(
      {
        tag: EXTENSION_GATE_MESSAGE_TAG,
        kind: "request",
        id,
        url,
        method: "POST",
        contentType,
        body: bytesToBase64(body),
        timeoutMs,
      },
      window.location.origin,
    );
  });
};
